"use client";

import type { UCService } from "@repo/design-system/types/global";
import { useCallback, useEffect, useState } from "react";

/**
 * Hook from the old Repo
 * This hook monitors the consent status of a specific service in Usercentrics
 * @param serviceName (ex: "YouTube Video")
 * @returns {consentStatus, refreshConsentStatus} (Status atual and function to manually update)
 * getServicesBaseInfo() returns a Promise that resolves to an array of services
 */

function useUCConsent(serviceName: string) {
  const [consentStatus, setConsentStatus] = useState(false);

  const tryToSetConsentStatus = useCallback(async () => {
    if (typeof window !== "undefined" && window.UC_UI) {
      try {
        const servicesBaseInfo =
          (await window.UC_UI.getServicesBaseInfo()) as UCService[];
        const service = servicesBaseInfo.find((x) => x.name === serviceName);
        const status = service?.consent.status ?? false;
        setConsentStatus(status);
        return status;
      } catch {
        setConsentStatus(false);
        return false;
      }
    }

    setConsentStatus(false);
    return false;
  }, [serviceName]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let waitTime = 1000;

    function checkConsentStatusWithTimeout() {
      tryToSetConsentStatus().catch(() => {
        setConsentStatus(false);
      });
      waitTime *= 1.5;
      timeoutId = setTimeout(checkConsentStatusWithTimeout, waitTime);
    }

    checkConsentStatusWithTimeout();

    return () => clearTimeout(timeoutId);
  }, [tryToSetConsentStatus]);

  const refreshConsentStatus = useCallback(
    () => tryToSetConsentStatus(),
    [tryToSetConsentStatus]
  );

  return { consentStatus, refreshConsentStatus };
}

export default useUCConsent;
